/* ============================================================================
   quick-language-switch.js
   Turns the header language control into a single-click toggle.

   The theme's control opens a "Region and language" drawer. With exactly two
   published languages that drawer is one step too many, so we intercept the
   click in the capture phase — before the theme's own <details>/@click handler
   sees it — and post Shopify's localization form straight away.

   Falls back silently to the theme's normal behaviour if anything is missing.
   ========================================================================== */
(function () {
  'use strict';

  var configEl = document.querySelector('[data-quick-language-config]');
  var form = document.querySelector('[data-quick-language-form]');
  if (!configEl || !form) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config || !config.target) return;

  /* Every element the theme uses to open the localization surface. */
  var TRIGGERS = [
    '.traditional-localization-drawer__trigger',
    '.bar-localization-drawer__trigger',
    '[data-header-surface="localization"] > summary',
    '[data-header-surface="localization"] summary.menu-drawer__menu-item',
    '.drawer-localization__button',
    'locale-dropdown .language-selector',
    '[data-quick-language-trigger]'
  ].join(',');

  function triggerFor(target) {
    if (!target || !target.closest) return null;
    return target.closest(TRIGGERS);
  }

  function switchLanguage() {
    form.submit();
  }

  /* Listen on `window`, not `document`. The theme registers its own delegated
     capture listener on `document` from fc-core.js, which loads first — same
     node + same phase means registration order decides, and we would lose.
     `window` sits one step higher in the capture path, so we always run first
     and `stopImmediatePropagation` keeps the drawer from opening. */
  window.addEventListener(
    'click',
    function (event) {
      var trigger = triggerFor(event.target);
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      switchLanguage();
    },
    true
  );

  /* Keyboard parity: <summary> opens on Enter/Space too. */
  window.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      var trigger = triggerFor(event.target);
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      switchLanguage();
    },
    true
  );

  /* Relabel the control so it advertises where the click will take you, and
     stop it announcing itself as a dialog opener. */
  function relabel() {
    var triggers = document.querySelectorAll(TRIGGERS);
    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.setAttribute('role', 'button');
      trigger.removeAttribute('aria-haspopup');
      trigger.removeAttribute('aria-expanded');
      trigger.setAttribute('aria-label', config.ariaLabel + ': ' + config.targetName);
      trigger.setAttribute('title', config.targetName);
      trigger.setAttribute('data-quick-language-ready', 'true');

      if (!config.showTargetLabel) return;

      /* The language code lives in the last bare <span> of the trigger. */
      var spans = trigger.querySelectorAll('span');
      for (var i = spans.length - 1; i >= 0; i--) {
        var span = spans[i];
        if (span.classList.length) continue;
        if (span.textContent.trim() === '/') continue;
        if (span.textContent.trim() === config.targetShort) break;
        span.textContent = config.targetShort;
        break;
      }
    });

    if (!config.showTargetLabel) return;

    /* Same label inside the mobile menu drawer. */
    var drawerLabels = document.querySelectorAll('.menu-drawer__locale-label span, .menu-drawer__locale-label');
    Array.prototype.forEach.call(drawerLabels, function (label) {
      if (label.children.length) return;
      var text = label.textContent.trim();
      if (text === config.targetShort) return;
      if (text !== config.currentShortGuess && text.length > 4) return;
      label.textContent = config.targetShort;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relabel, { once: true });
  } else {
    relabel();
  }

  document.addEventListener('shopify:section:load', relabel);
  window.addEventListener('load', relabel);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(relabel);

  /* The theme re-renders the header (sticky handling, section morphing), which
     wipes the attributes we set. Re-apply whenever the header subtree changes. */
  (function observeHeader() {
    var host = document.querySelector('#fit-header-group') || document.body;
    if (!host || typeof MutationObserver === 'undefined') return;
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        observer.disconnect();
        relabel();
        observer.observe(host, { childList: true, subtree: true });
      });
    });
    observer.observe(host, { childList: true, subtree: true });
  })();

  window.FitCheckQuickLanguageSwitch = { switchLanguage: switchLanguage, config: config };
})();
